import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ThemeProvider } from '@bp-files/ui';
import { env } from '@/env';
import { Bootstrap } from '@/Bootstrap';
import { mockSignInDecided, setMockSignedIn } from '@/lib/mockSignedIn';
// Keyframes and the scrollbar treatment that @bp-files/ui components rely on.
import '@bp-files/ui/styles.css';
import './index.css';

/**
 * Entry point.
 *
 * `env` is imported first and deliberately: it validates at module load, so a
 * misconfigured app fails immediately with a readable message rather than
 * rendering a broken shell.
 *
 * **Sign-in is no longer here.** It used to complete inside `boot()`, before
 * `createRoot`, which is what made the redirect round trip a blank frame and
 * left a failed sign-in with nowhere to be reported. `Bootstrap` renders the
 * gate, the gate signs in, and `<BrowserRouter>` mounts below it — see
 * `Bootstrap.tsx` for why the router had to move too (ADR-0034, ADR-0027).
 */

async function startMocks(): Promise<void> {
  if (!env.VITE_USE_MOCKS) return;

  /*
    **Seed the mocked tab's sign-in flag before the worker answers anything.**
    `VITE_MOCK_SIGNED_IN` is the *initial* value, not the current one — once this
    tab has signed in or out, what it did last wins — so this applies once per
    tab rather than once per load, or the reload that completes the mocked
    sign-in would put the flag straight back.

    **Here rather than in `mockAuth.ts`, which is where it used to be read.**
    The mocked `GET /me` needs the same answer (ADR-0050) and `handlers.ts` can
    reach no module that touches `@/env` — the Playwright specs import it in
    Node, where `import.meta.env` does not exist. This is the one place that
    already knows both that this is a mocked build and what the env says.
  */
  if (!mockSignInDecided()) setMockSignedIn(env.VITE_MOCK_SIGNED_IN);

  const { worker } = await import('@/test/mocks/browser');
  await worker.start({ onUnhandledRequest: 'bypass' });
}

function mount(): void {
  const container = document.getElementById('root');
  if (!container) {
    throw new Error('Root element #root is missing from index.html.');
  }

  createRoot(container).render(
    <StrictMode>
      {/*
        `system` — the only place in the repo that opts into it (ADR-0064).
        `ThemeProvider` defaults to `dark` so that `apps/admin` and every test
        that wraps one keeps the command room; this app has a switcher on
        General, and "match this device" is the right thing for a first visit to
        do when nobody has chosen yet.
      */}
      <ThemeProvider defaultPreference="system">
        <Bootstrap />
      </ThemeProvider>
    </StrictMode>,
  );
}

/*
  The worker still starts before the first render, and it has to: the gate
  prefetches `GET /me` the moment it has a session, and a worker that is not
  listening yet means that request escapes to a nonexistent API.

  **Render anyway on failure, which is now a much smaller claim than it was.** A
  failed mock worker must not block the app; that is all this catch covers now.
  A failed sign-in has its own screen inside the gate.
*/
startMocks()
  .catch((error: unknown) => {
    console.error('Could not start the mock worker.', error);
  })
  .finally(mount);
